import React, { useState, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import { useBarcodeScanner } from './useBarcodeScanner';
import PalletDetailCard, { PalletData } from './PalletDetailCard';
import ManualScanInput from './ManualScanInput';
import { api } from '../../config/api';

type ConnectionStatus = 'idle' | 'success' | 'error';

interface AisleRecommendation {
  aisle: string;
  available_spaces: number;
  has_active_vna: boolean;
}

/**
 * iPad-optimized pallet intake screen.
 *
 * Operators scan SSCC barcodes at the B3/B4 boundary. The screen shows a
 * detail card with job/customer/product information after each scan,
 * plus an aisle drop recommendation and drop zone scan confirmation.
 *
 * No manual SSCC input -- scanner-only workflow.
 */
export default function IntakeScreen() {
  const [palletData, setPalletData] = useState<PalletData | null>(null);
  const [lastSscc, setLastSscc] = useState<string | null>(null);
  const lastSsccRef = useRef<string | null>(null);
  const [scanCount, setScanCount] = useState(0);
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showScanFlash, setShowScanFlash] = useState(false);
  const [aisleRecommendation, setAisleRecommendation] = useState<AisleRecommendation | null>(null);
  const [recommendationLoaded, setRecommendationLoaded] = useState(false);
  const [dropConfirmed, setDropConfirmed] = useState(false);
  const [dropAisle, setDropAisle] = useState<string | null>(null);

  const fetchAisleRecommendation = useCallback(async () => {
    try {
      const response = await api.get('/wms/putaway/aisle-recommendation');
      if (response.data.success && response.data.data) {
        setAisleRecommendation(response.data.data);
      } else {
        setAisleRecommendation(null);
      }
    } catch {
      setAisleRecommendation(null);
    }
    setRecommendationLoaded(true);
  }, []);

  const handleDropZoneScan = useCallback(async (code: string) => {
    if (!lastSsccRef.current) return;

    try {
      const response = await api.post('/wms/putaway/drop-confirm', {
        sscc: lastSsccRef.current,
        drop_zone_code: code,
      });
      if (response.data.success) {
        setDropConfirmed(true);
        setDropAisle(response.data.data.aisle);
        setPalletData((prev) => prev ? { ...prev, status: 'at_drop_zone' } : prev);
      } else {
        setErrorMessage(response.data.error || 'Drop confirmation failed');
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to confirm drop';
      setErrorMessage(msg);
    }
  }, []);

  const handleIntakeScan = useCallback(async (sscc: string) => {
    // Reset drop state for new pallet
    setDropConfirmed(false);
    setDropAisle(null);
    setRecommendationLoaded(false);
    setAisleRecommendation(null);

    try {
      const response = await api.post('/wms/intake', { sscc });
      if (response.data.success) {
        setPalletData(response.data.data);
        setLastSscc(sscc);
        lastSsccRef.current = sscc;
        setScanCount((prev) => prev + 1);
        setStatus('success');
        setErrorMessage(null);
        // Fetch aisle recommendation after successful intake
        fetchAisleRecommendation();
      } else {
        setStatus('error');
        setErrorMessage(response.data.error || 'Intake failed');
      }
    } catch (err: any) {
      setStatus('error');
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to process scan';
      setErrorMessage(msg);
    }
  }, [fetchAisleRecommendation]);

  const handleScan = useCallback((code: string) => {
    // Flash "Scanned!" briefly
    setShowScanFlash(true);
    setTimeout(() => setShowScanFlash(false), 800);

    // Distinguish SSCC (18+ digits) from drop zone code (shorter, contains letters)
    const isSSCC = code.length >= 18 && /^\d+$/.test(code);

    if (isSSCC) {
      handleIntakeScan(code);
    } else {
      handleDropZoneScan(code);
    }
  }, [handleIntakeScan, handleDropZoneScan]);

  useBarcodeScanner({ minLength: 4, onScan: handleScan });

  const statusColor =
    status === 'success'
      ? '#2E7D32'
      : status === 'error'
        ? '#D32F2F'
        : '#9E9E9E';

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: 'auto',
        width: '100%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Connection status indicator — layout provides the page title */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: statusColor,
          }}
          data-testid="status-dot"
          aria-label={`Connection ${status}`}
        />
      </Box>

      {/* Scan feedback area */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          textAlign: 'center',
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'background-color 0.2s',
        }}
      >
        {showScanFlash ? (
          <Fade in={showScanFlash}>
            <Typography
              variant="h5"
              sx={{ color: '#2E7D32', fontWeight: 600 }}
            >
              Scanned!
            </Typography>
          </Fade>
        ) : (
          <Typography variant="body1" color="text.secondary">
            {scanCount === 0
              ? 'Waiting for scan...'
              : `Scan count: ${scanCount} pallets this session`}
          </Typography>
        )}
      </Paper>

      {/* Manual barcode entry for desktop testing */}
      <Box sx={{ mb: 3 }}>
        <ManualScanInput onSubmit={handleScan} placeholder="Type or paste SSCC / drop zone code..." />
      </Box>

      {/* Pallet detail card - appears after first scan */}
      {palletData && (
        <Box sx={{ mb: 3 }}>
          <PalletDetailCard data={palletData} />
        </Box>
      )}

      {/* Putaway recommendation section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mt: 'auto',
          textAlign: 'center',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          backgroundColor: dropConfirmed ? '#E8F5E9' : '#FFFFFF',
          border: dropConfirmed ? '2px solid #2E7D32' : '1px solid #E0E0E0',
          transition: 'background-color 0.2s',
        }}
      >
        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
          PUTAWAY RECOMMENDATION
        </Typography>

        {!palletData && !dropConfirmed && (
          <Typography variant="body2" color="text.secondary">
            Scan a pallet to see drop recommendation
          </Typography>
        )}

        {palletData && !dropConfirmed && !recommendationLoaded && (
          <Typography variant="body2" color="text.secondary">
            Loading recommendation...
          </Typography>
        )}

        {palletData && !dropConfirmed && recommendationLoaded && aisleRecommendation && (
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: '#CB6015', mb: 1 }}
            >
              DROP AT AISLE {aisleRecommendation.aisle}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
              Drop zone: 4{aisleRecommendation.aisle}DROP
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Chip
                label={`${aisleRecommendation.available_spaces} spaces available`}
                color="primary"
                variant="outlined"
              />
              {aisleRecommendation.has_active_vna && (
                <Chip
                  label="VNA Active"
                  sx={{ backgroundColor: '#2E7D32', color: '#FFFFFF' }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Scan drop zone barcode to confirm
            </Typography>
          </Box>
        )}

        {palletData && !dropConfirmed && recommendationLoaded && !aisleRecommendation && (
          <Alert severity="warning" sx={{ textAlign: 'left' }}>
            No drop bays available
          </Alert>
        )}

        {dropConfirmed && dropAisle && (
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: '#2E7D32', mb: 1 }}
            >
              Dropped at Aisle {dropAisle}
            </Typography>
            <Chip
              label="Drop confirmed"
              sx={{ backgroundColor: '#2E7D32', color: '#FFFFFF' }}
            />
          </Box>
        )}
      </Paper>

      {/* Error snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
